<script>
import moment from "moment";
import Swal from "sweetalert2";
import Drawer from "@/components/drawer";

// Custom Components
import BookingDetails from "@/components/booking-details.vue";
import GuestService from "@/helpers/APIService/guest.service";
import errorHandler from "@/helpers/errorHandler";
import Middleware from "@/helpers/middlewares";
import { authComputed } from "@/state/helpers";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Wakeup Call List",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      status: true,
      selectedBooking: "",
      viewBookingDetails: false,
      table: {
        tableData: [],
        size: 100,
        next: null,
        previous: null,
      },
      isDataLoading: false,
    };
  },
  components: { BookingDetails, Drawer },
  computed: {
    ...authComputed,
    bookingDetailsProps() {
      return {
        booking: { id: this.selectedBooking },
      };
    },
  },
  mounted() {
    this.getCallData("?size=" + this.table.size);
  },
  methods: {
    async openBookingDetails(data) {
      if (data != null) {
        this.selectedBooking = data;
      }
      this.viewBookingDetails = !this.viewBookingDetails;
    },
    async getCallData(query) {
      try {
        this.isDataLoading = true;
        const response = await GuestService.getWakeUpCallList(query);
        if (response) {
          this.table.tableData = response.results;
          this.table.previous = response.previous;
          this.table.next = response.next;
          this.isDataLoading = false;
        }
      } catch (error) {
        this.isDataLoading = false;
        errorHandler(error);
      }
    },
    // Method is used to get more guest
    loadMore: function () {
      if (this.table.next !== null) {
        const str = this.table.next.split("?");
        this.getCallData(`?${str[1]}`);
      }
    },
    convertTime(data) {
      return moment(data).format("HH:mm A");
    },
    async editWakeUp(id, data) {
      data.status = !data.status;
      const newData = new FormData();
      newData.append("is_active", data.status);
      try {
        let response = GuestService.editWakeUpCall(id, newData);
        if (response) {
          Middleware.notification("Status changed successfully !!", "success");
        }
      } catch (error) {
        errorHandler(error);
      }
    },
    async deleteWakeUp(id) {
      Swal.fire({
        title: "Do you want to delete wake-up call?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Confirm!",
      }).then((result) => {
        if (result.value) {
          try {
            const response = GuestService.deleteWakeUpCall(id);
            if (response) {
              Middleware.notification("Wake-up call deleted !!", "danger");
              this.getCallData();
            }
          } catch (error) {
            errorHandler(error);
          }
        } else {
          Middleware.notification("Deletion cancelled !!", "success");
        }
      });
    },
  },
};
</script>
<template>
  <div>
    <div
      class="row"
      v-infinite-scroll="loadMore"
      infinite-scroll-disabled="isDataLoading"
      infinite-scroll-distance="100"
    >
      <b-card>
        <div class="table-responsive">
          <v-table
            :data="table.tableData"
            class="table table-hover mb-0"
            ref="my-table"
          >
            <thead class="table-light" slot="head">
              <tr>
                <th>Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>WakeUp Time</th>
                <th>Call Status</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody v-if="isDataLoading">
              <tr v-for="item in 13" :key="item">
                <td class="placeholder-glow" v-for="no in 7" :key="no">
                  <span class="placeholder w-100 placeholder-lg"></span>
                </td>
              </tr>
            </tbody>
            <tbody
              class="infinite-list"
              slot="body"
              slot-scope="{ displayData }"
              v-if="!isDataLoading && table.tableData.length !== 0"
            >
              <tr v-for="data in displayData" :key="data.id">
                <td class="text-left" @click="openBookingDetails(data.booking)">
                  {{ data.guest.last_name ? data.guest.last_name : "" }}
                  {{ data.guest.first_name ? data.guest.first_name : "" }}
                </td>
                <td
                  class="text-capitalize"
                  @click="openBookingDetails(data.booking)"
                >
                  {{ data.mobile }}
                </td>
                <td class="" @click="openBookingDetails(data.booking)">
                  <span>{{ data.guest.email ? data.guest.email : "-" }}</span>
                  <i></i>
                </td>

                <td class="text-capitalize">
                  {{
                    data.wakeup_datetime
                      ? convertTime(data.wakeup_datetime)
                      : "00"
                  }}
                </td>

                <td class="text-capitalize">
                  <span
                    class="badge align-items-center py-1 px-2 card-badge"
                    :class="[
                      data.pbx_webhook_response.callstatus == 'NORMAL_CLEARING'
                        ? 'badge-soft-success'
                        : data.pbx_webhook_response.callstatus == 'NO_ANSWER'
                        ? 'badge-soft-danger'
                        : 'badge-soft-primary',
                    ]"
                  >
                    {{
                      data.pbx_webhook_response
                        ? data.pbx_webhook_response.callstatus ==
                          "NORMAL_CLEARING"
                          ? "Normal Clearing"
                          : data.pbx_webhook_response.callstatus == "NO_ANSWER"
                          ? "No Answer"
                          : "-"
                        : "-"
                    }}</span
                  >
                </td>
                <td>
                  <div class="form-check form-switch my-1" slot="is_active">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="data.is_active"
                      @click="
                        editWakeUp(data.id, {
                          status: data.is_active,
                        })
                      "
                    />
                  </div>
                </td>
                <td>
                  <i
                    class="fas fa-trash mx-1"
                    @click="deleteWakeUp(data.id)"
                  ></i>
                </td>
              </tr>
            </tbody>
            <tbody v-if="!isDataLoading && table.tableData.length == 0">
              <tr>
                <td colspan="9" class="text-center">
                  <img
                    src="@/assets/images/empty.svg"
                    class="img-fluid"
                    alt=""
                  />
                  <p>
                    <span class="text-secondary">No Data Found</span>
                  </p>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </b-card>
    </div>
    <Drawer
      @close="openBookingDetails"
      :align="'right'"
      :closeable="true"
      :maskClosable="true"
      :zIndex="1002"
      :maxWidth="1240"
      class="w-100"
    >
      <div v-if="viewBookingDetails">
        <BookingDetails :currentProps="bookingDetailsProps" />
      </div>
    </Drawer>
  </div>
</template>
